import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { isAffectedForCurrentPeriod } from '@/lib/disponibilite-utils';
import { queueOrExecuteInvalidation } from '@/lib/modalProtection';

// Type pour jours_details JSONB
interface JourDetail {
  date: string;
  period: 'AM' | 'PM' | 'FULL';
  value: number;
}

export interface MembreDisponibilite {
  id: string;
  nom: string;
  prenom: string;
  poste: string | null;
  specialite: string | null;
  taux_horaire: number;
  charges_salariales: number;
  charges_patronales: number;
  coefficient_charges: number;
  statut: string;
  entreprise_id: string;
  equipe_id: string | null;
  equipe_nom: string | null;
  equipe_specialite: string | null;
  equipe_pictogramme: string | null;
  equipe_couleur: string | null;
  affectation_id: string | null;
  chantier_id: string | null;
  chantier_actuel: string | null;
  reference_chantier: string | null;
  chantier_pictogramme: string | null;
  chantier_couleur: string | null;
  date_debut: string | null;
  date_fin: string | null;
  heures_par_jour: number | null;
  taux_horaire_specifique: number | null;
  jours_details: JourDetail[] | null; // Détails des jours travaillés (AM/PM/FULL)
  disponibilite: 'disponible' | 'affecte';
  is_retard: boolean; // true si c'est une affectation de retard
}

interface UseDisponibilitesProps {
  entrepriseId: string | null;
}

export function useDisponibilites({ entrepriseId }: UseDisponibilitesProps) {
  const [membres, setMembres] = useState<MembreDisponibilite[]>([]);
  const [loading, setLoading] = useState(true);

  // silentRefresh = true → ne pas afficher de loader (pour refresh en arrière-plan via Realtime)
  const loadDisponibilites = useCallback(async (silentRefresh = false) => {
    if (!entrepriseId) return;

    // Ne pas afficher le loader si c'est un refresh silencieux
    if (!silentRefresh) {
      setLoading(true);
    }
    try {
      // Requête directe sur membres_equipe avec jointures
      // Car la vue peut ne pas être encore créée en base
      const { data, error } = await supabase
        .from('membres_equipe')
        .select(`
          id,
          nom,
          prenom,
          poste,
          specialite,
          taux_horaire,
          coefficient_charges,
          heures_par_jour,
          statut,
          entreprise_id,
          equipe_id,
          equipe:equipes(nom, specialite, pictogramme, couleur)
        `)
        .eq('entreprise_id', entrepriseId)
        .in('statut', ['disponible', 'sur_chantier'])
        .order('nom');

      if (error) throw error;

      // Charger les affectations actives (inclut les affectations de retard)
      // Exclure les affectations de chantiers suspendus (etat_chantier = 'suspendu')
      const { data: affectations } = await supabase
        .from('affectations_chantiers')
        .select(`
          id,
          membre_equipe_id,
          chantier_id,
          date_debut,
          date_fin,
          heures_par_jour,
          taux_horaire_specifique,
          jours_details,
          notes,
          chantier:chantiers!inner(nom_chantier, reference_chantier, pictogramme, couleur, etat_chantier)
        `)
        .gte('date_fin', new Date().toISOString().split('T')[0])
        .lte('date_debut', new Date().toISOString().split('T')[0])
        .neq('chantiers.etat_chantier', 'suspendu');

      // Mapper les données
      const today = new Date().toISOString().split('T')[0];
      const membresAvecDispo: MembreDisponibilite[] = (data || []).map((m: any) => {
        const affectation = affectations?.find((a: any) => a.membre_equipe_id === m.id);
        // Vérifier si c'est une affectation de retard via le champ notes
        const isRetard = affectation?.notes?.startsWith('Retard chantier') || false;

        // Vérifier si l'affectation couvre la période actuelle (AM/PM)
        // Utilise jours_details pour déterminer si le membre est affecté matin, après-midi ou journée complète
        const isCurrentlyAffected = affectation
          ? isAffectedForCurrentPeriod(affectation.jours_details, today)
          : false;

        return {
          id: m.id,
          nom: m.nom,
          prenom: m.prenom,
          poste: m.poste,
          specialite: m.specialite,
          taux_horaire: m.taux_horaire || 0,
          charges_salariales: 0, // DEPRECATED - utiliser coefficient_charges
          charges_patronales: 0, // DEPRECATED - utiliser coefficient_charges
          coefficient_charges: m.coefficient_charges || 1.85,
          statut: m.statut,
          entreprise_id: m.entreprise_id,
          equipe_id: m.equipe_id,
          equipe_nom: m.equipe?.nom || null,
          equipe_specialite: m.equipe?.specialite || null,
          equipe_pictogramme: m.equipe?.pictogramme || null,
          equipe_couleur: m.equipe?.couleur || null,
          // Si pas affecté pour la période actuelle, ne pas retourner les infos d'affectation
          affectation_id: isCurrentlyAffected ? affectation?.id : null,
          chantier_id: isCurrentlyAffected ? affectation?.chantier_id : null,
          chantier_actuel: isCurrentlyAffected ? affectation?.chantier?.nom_chantier : null,
          reference_chantier: isCurrentlyAffected ? affectation?.chantier?.reference_chantier : null,
          chantier_pictogramme: isCurrentlyAffected ? affectation?.chantier?.pictogramme : null,
          chantier_couleur: isCurrentlyAffected ? affectation?.chantier?.couleur : null,
          date_debut: isCurrentlyAffected ? affectation?.date_debut : null,
          date_fin: isCurrentlyAffected ? affectation?.date_fin : null,
          heures_par_jour: isCurrentlyAffected ? affectation?.heures_par_jour : null,
          taux_horaire_specifique: isCurrentlyAffected ? affectation?.taux_horaire_specifique : null,
          jours_details: affectation?.jours_details || null,
          disponibilite: isCurrentlyAffected ? 'affecte' : 'disponible',
          is_retard: isCurrentlyAffected ? isRetard : false,
        };
      });

      setMembres(membresAvecDispo);
    } catch (error) {
      console.error('Erreur chargement disponibilités:', error);
    } finally {
      // Ne pas mettre loading = false si c'était un refresh silencieux
      if (!silentRefresh) {
        setLoading(false);
      }
    }
  }, [entrepriseId]);

  useEffect(() => {
    loadDisponibilites();
  }, [loadDisponibilites]);

  // Subscription realtime
  useEffect(() => {
    if (!entrepriseId) return;

    const channel = supabase
      .channel(`disponibilites-realtime:${entrepriseId}`)
      .on(
        'postgres_changes',
        // affectations_chantiers n'a pas de colonne entreprise_id — RLS filtre côté serveur
        { event: '*', schema: 'public', table: 'affectations_chantiers' },
        () => queueOrExecuteInvalidation(() => loadDisponibilites(true))
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'membres_equipe', filter: `entreprise_id=eq.${entrepriseId}` },
        () => queueOrExecuteInvalidation(() => loadDisponibilites(true))
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [entrepriseId, loadDisponibilites]);

  // Filtres pratiques
  const membresDisponibles = membres.filter((m) => m.disponibilite === 'disponible');
  const membresAffectes = membres.filter((m) => m.disponibilite === 'affecte');

  return {
    membres,
    membresDisponibles,
    membresAffectes,
    loading,
    refresh: loadDisponibilites,
  };
}
